import BaseComponent from "./base_component_controller";

import { useChartConfig } from "hb/insights/use_chart_config";
import { DEFAULT_QUERY } from "hb/insights/query";
import { useOutlets } from "hb/insights/use_outlets";

import hljs from "highlight.js/lib/core";

const JSON_PREVIEW_MAX_CHARS = 500;
// Rows highlighted synchronously during initial render. Sized to fully cover
// the first viewport at common heights so the first paint shows colored
// JSON rather than plain text → flash → color.
const JSON_PREHIGHLIGHT_ROWS = 30;

export default class extends BaseComponent {
  static viewId = "table";
  static targets = [
    "eventResults",
    "eventWithIdTemplate",
    "error",
    "eventTemplate",
    "eventList",
    "eventHeaders",
    "headerTemplate",
    "errorMessageTemplate",
    "emptyQueryTemplate",
  ];

  static values = {
    querySessionId: String,
    schema: Array,
    fields: Array,
    results: Array,
  };

  connect() {
    super.connect();

    useOutlets(this, {
      components: ["handsfree"],
      scope: this.element.closest(".handsfree-parent"),
    });

    this.listen("panels:dragstart", this.pinColumns, document);
    this.listen("panels:dragend", this.unpinColumns, document);

    this.eventWithIdTemplate = this.compileElement(
      this.eventWithIdTemplateTarget,
    );
    this.eventTemplate = this.compileElement(this.eventTemplateTarget);
    this.headerTemplate = this.compileElement(this.headerTemplateTarget);
    this.gridColumns = [];

    // Use the table's actual scrolling ancestor as root (typically
    // .main-panel) so the 1500px margin extends that container's
    // clip — root: null would be clipped by overflow:auto ancestors
    // and never fire for off-screen rows.
    this.jsonHighlighter = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this._highlightJsonCell(entry.target);
          this.jsonHighlighter.unobserve(entry.target);
        });
      },
      { root: this._scrollAncestor(this.element), rootMargin: "1500px 0px" },
    );

    if (this.hasEventListTarget) {
      this.listenRaw("click", this._delegatedClick, this.eventListTarget);
      this.listenRaw("mouseover", this._delegatedMouseover, this.eventListTarget);
      this.listenRaw("mouseout", this._delegatedMouseout, this.eventListTarget);
    }

    useChartConfig(this);

    this.initQuery();
  }

  disconnect() {
    this.jsonHighlighter?.disconnect();
    super.disconnect?.();
  }

  _scrollAncestor(el) {
    let p = el;
    while (p && p !== document.body) {
      const overflowY = getComputedStyle(p).overflowY;
      if (overflowY === "auto" || overflowY === "scroll") return p;
      p = p.parentElement;
    }
    return null;
  }

  _highlightJsonCell(cell) {
    const span = cell.querySelector(":scope > span");
    if (!span || span.dataset.hljsApplied === "true") return;
    span.innerHTML = hljs.highlight(span.textContent, {
      language: "json",
    }).value;
    span.dataset.hljsApplied = "true";
  }

  pinColumns() {
    if (!this.hasEventResultsTarget) return;
    const grid = this.eventResultsTarget;
    const computed = getComputedStyle(grid).gridTemplateColumns;
    if (!computed || computed === "none") return;
    this._savedGridTemplateColumns = grid.style.gridTemplateColumns;
    grid.style.gridTemplateColumns = computed;
  }

  unpinColumns() {
    if (!this.hasEventResultsTarget) return;
    this.eventResultsTarget.style.gridTemplateColumns =
      this._savedGridTemplateColumns ?? "";
    this._savedGridTemplateColumns = undefined;
  }

  _delegatedClick(evt) {
    const field = evt.target.closest(".field");
    if (!field || !this.eventListTarget.contains(field)) return;
    if (!window.getSelection().isCollapsed) return;
    const { fieldType, fieldName, fieldValue } = field.dataset;
    if (fieldType === "json") {
      const item = field.closest(".event-content");
      if (item) {
        this._hydrateRawEvent(item);
        item.classList.toggle("expanded");
      }
    } else {
      this.showHandsfreeDropdown(fieldType, fieldName, fieldValue);
    }
  }

  _isDatetimeField(el) {
    if (!el) return false;
    const t = el.dataset?.fieldType;
    return t === "datetime" || (t && t.startsWith("tzdatetime"));
  }

  _delegatedMouseover(evt) {
    const field = evt.target.closest(".field");
    if (!field || !this._isDatetimeField(field)) return;
    if (field === this._currentHoverField) return;
    this._currentHoverField = field;
    window.dispatchEvent(
      new CustomEvent("chart:crosshairHighlight", {
        detail: { timestamp: field.dataset.fieldValue },
      }),
    );
  }

  _delegatedMouseout(evt) {
    const fromField = evt.target.closest(".field");
    if (!fromField || !this._isDatetimeField(fromField)) return;
    const toField = evt.relatedTarget?.closest?.(".field");
    if (this._isDatetimeField(toField)) return;
    this._currentHoverField = null;
    window.dispatchEvent(new Event("chart:clearCrosshair"));
  }

  colReset({ params: { colIdx } }) {
    this.gridColumns[colIdx - 1] = "auto";
    this.updateGrid();
  }

  colDrag({ pageX, params: { colIdx }, target }) {
    const parentNode = target.parentNode;
    const prevNode = parentNode.previousElementSibling;
    const startWidth = parseInt(getComputedStyle(prevNode).width, 10);

    target.classList.add("dragging");

    const resizeFn = (evt) => {
      const newWidth = startWidth + (evt.pageX - pageX);
      this.gridColumns[colIdx - 1] = `${newWidth}px`;
      this.updateGrid();
    };

    const stopFn = () => {
      target.classList.remove("dragging");
      document.removeEventListener("mousemove", resizeFn);
      document.removeEventListener("mouseup", stopFn);
    };

    document.addEventListener("mousemove", resizeFn);
    document.addEventListener("mouseup", stopFn);
  }

  showHandsfreeDropdown(type, name, value) {
    this.outletComponent("handsfree").show(type, name, value, {
      element: this.element,
      excludeTypeFromField: true,
    });
  }

  updateGrid() {
    this.eventResultsTarget.style.gridTemplateColumns =
      this.gridColumns.join(" ");
  }

  toggleMeta(evt) {
    const item = this.eventListTarget.querySelector(
      `#event-${evt.params.eventId}`,
    );
    if (!item) return;
    this._hydrateRawEvent(item);
    item.classList.toggle("expanded");
  }

  _hydrateRawEvent(item) {
    const slot = item.querySelector(".raw-event");
    if (!slot || slot.dataset.hydrated === "true") return;
    const baseUrl = this.eventListTarget.dataset.eventBaseUrl;
    if (!baseUrl) return;
    const { eventId, streamId, ts } = slot.dataset;
    const params = new URLSearchParams({
      id: eventId,
      stream_id: streamId,
      timestamp: ts,
    });
    const frame = document.createElement("turbo-frame");
    frame.id = `event-${eventId}`;
    frame.setAttribute("src", `${baseUrl}?${params}`);
    frame.setAttribute("loading", "lazy");
    frame.appendChild(Object.assign(document.createElement("div"), { textContent: "Loading" }));
    slot.appendChild(frame);
    slot.dataset.hydrated = "true";
  }

  onQueryReady(evt) {
    super.onQueryReady(evt);

    const {
      detail: { query },
    } = evt;
    this.query = query.data.response.query;
  }

  onQueryEmpty() {
    this.hasEventListTarget && this.eventListTarget.classList.add("hidden");
    this.hasEventResultsTarget &&
      this.eventResultsTarget.classList.add("hidden");

    const colorQuery = hljs.highlight(DEFAULT_QUERY, {
      language: "badgerql",
    }).value;
    this.errorTarget.classList.add("show");
    const emptyTemplate = this.compileElement(this.emptyQueryTemplateTarget);
    this.errorTarget.innerHTML = emptyTemplate({ query: colorQuery });
  }

  _render() {
    if (this.errorMessage !== null) {
      this.hasEventResultsTarget &&
        this.eventResultsTarget.classList.add("hidden");
      this.hasEventListTarget && this.eventListTarget.classList.add("hidden");
      this.errorTarget.classList.add("show");
      const errorMessageTemplate = this.compileElement(
        this.errorMessageTemplateTarget,
      );
      this.errorTarget.innerHTML = errorMessageTemplate({
        message: this.errorMessage,
        showConfigMessage: this.hideConfigMessage,
      });
    } else {
      this.errorTarget.classList.remove("show");
      this.hasEventResultsTarget &&
        this.eventResultsTarget.classList.remove("hidden");
      this.hasEventListTarget &&
        this.eventListTarget.classList.remove("hidden");
    }

    super._render();
  }

  render() {
    super.connect();

    const {
      query: { meta, results },
    } = this;

    this.eventListTarget.replaceChildren();
    this.eventHeadersTarget.replaceChildren();

    if (results?.length > 0) {
      this.gridColumns = meta.fields.map(() => "auto");
      let withId = false;

      if (meta.has("@id")) {
        withId = true;
        this.element.classList.add("with-id");
        this.gridColumns.unshift("40px");
        this.eventHeadersTarget.append(document.createElement("div"));
      } else {
        this.element.classList.remove("with-id");
      }

      // Add empty header if there are no returned fields
      if (!meta.hasFields) {
        const header = document.createElement("div");
        header.innerHTML = this.headerTemplate({
          field: "",
          allowsDragging: false,
          index: 1,
        });
        this.eventHeadersTarget.append(header);
      } else {
        meta.fields.forEach((field, index) => {
          const header = document.createElement("div");
          header.innerHTML = this.headerTemplate({
            field,
            allowsDragging: index > 0,
            index: withId ? index + 1 : index,
            type: meta.shortType(field),
          });
          this.eventHeadersTarget.append(header);
        });
      }

      this.updateGrid();

      if (!meta.hasFields) {
        results.forEach(() => {
          const item = document.createElement("div");
          item.classList.add("event-content");
          this.eventListTarget.append(item);
        });
      } else {
        results.forEach((result, rowIdx) => {
          const item = document.createElement("div");
          item.classList.add("event-content");

          if (meta.has("@id") && !result.field("@fill")) {
            item.id = `event-${result.field("@id")}`;
            item.innerHTML = this.eventWithIdTemplate({
              id: result.field("@id"),
              ts: result.tsUTC(),
              org_id: result.field("@org.id"),
              stream_id: result.field("@stream.id"),
            });
          } else {
            item.innerHTML = this.eventTemplate({
              withFill: meta.has("@id") && result.field("@fill"),
            });
          }

          const fields = item.querySelector(".event-fields");

          meta.fields.forEach((name) => {
            const div = document.createElement("div");
            const span = document.createElement("span");
            div.appendChild(span);

            const field = result.field(name);
            const type = meta.type(name);

            div.dataset.fieldType = type;
            if (field !== null) {
              div.dataset.fieldValue = field;
            }
            div.dataset.fieldName = name;

            div.classList.add("field", `field-type-${type}`);
            if (type === "json") {
              div.classList.add("actionable");
              span.textContent =
                field.length > JSON_PREVIEW_MAX_CHARS
                  ? field.slice(0, JSON_PREVIEW_MAX_CHARS) + "…"
                  : field;
              if (rowIdx < JSON_PREHIGHLIGHT_ROWS) {
                this._highlightJsonCell(div);
              } else {
                this.jsonHighlighter.observe(div);
              }
            } else {
              span.textContent = field;
            }

            fields.append(div);
          });

          this.eventListTarget.append(item);
        });
      }
    }
  }
}
